/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Music, Headphones, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { keyOptions, chordOptions, scaleOptions, AccidentalPreference, isScaleAvailableForFree, isChordAvailableForFree, isKeyAvailableForFree } from '../musicOptions';

interface MobileControlBarProps {
  // Current selections
  currentKey: string;
  chordOrScale: string;
  isChord: boolean;
  appMode: 'scale' | 'chord' | 'earTraining' | 'compare';
  accidentalPreference: AccidentalPreference;

  // Handlers
  onKeyChange: (key: string) => void;
  onChordOrScaleChange: (value: string) => void;
  onModeChange: (mode: 'scale' | 'chord' | 'earTraining' | 'compare') => void;
  onAccidentalPreferenceChange: (pref: AccidentalPreference) => void;
  onSettingsClick: () => void;

  // Display helpers
  getDisplayName: (value: string, isChord: boolean) => string;

  // User state for gating features
  isAnonymous: boolean;
  isFree: boolean;
}

/**
 * MobileControlBar - Collapsible control bar for mobile devices
 *
 * Shows current selection in collapsed state, expands to full controls on tap.
 * Replaces the hidden hamburger menu approach with always-visible context.
 */
const MobileControlBar: React.FC<MobileControlBarProps> = ({
  currentKey,
  chordOrScale,
  isChord,
  appMode,
  accidentalPreference,
  onKeyChange,
  onChordOrScaleChange,
  onModeChange,
  onAccidentalPreferenceChange,
  onSettingsClick,
  getDisplayName,
  isAnonymous,
  isFree
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const displayName = getDisplayName(chordOrScale, isChord);
  const modeLabel = appMode === 'earTraining' ? 'Practice' : isChord ? 'Chord' : 'Scale';

  // Render key options with lock indicators
  const renderKeyOptions = () =>
    keyOptions.map(option => {
      const valueForPreference =
        accidentalPreference === 'flats' && option.flat ? option.flat : option.sharp;
      const isLocked = (isAnonymous || isFree) && !isKeyAvailableForFree(valueForPreference);

      return (
        <SelectItem
          key={`${option.sharp}-${valueForPreference}`}
          value={valueForPreference}
          disabled={isLocked}
          className={isLocked ? 'opacity-60' : ''}
        >
          <span className="flex items-center gap-2">
            {valueForPreference}
            {isLocked && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-indigo-500/10 text-indigo-600 border-indigo-500/30">
                Pro
              </Badge>
            )}
          </span>
        </SelectItem>
      );
    });

  // Render chord/scale options with lock indicators
  const renderChordScaleOptions = () =>
    (isChord ? chordOptions : scaleOptions).map(option => {
      const isAllowed = isChord
        ? isChordAvailableForFree(option.value)
        : isScaleAvailableForFree(option.value);
      const isLocked = (isAnonymous || isFree) && !isAllowed;

      return (
        <SelectItem
          key={option.value}
          value={option.value}
          disabled={isLocked}
          className={isLocked ? 'opacity-60' : ''}
        >
          <span className="flex items-center gap-2">
            {option.display}
            {isLocked && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-indigo-500/10 text-indigo-600 border-indigo-500/30">
                Pro
              </Badge>
            )}
          </span>
        </SelectItem>
      );
    });

  return (
    <div className="bg-card border-b shadow-sm">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        {/* Collapsed Header - Always Visible */}
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors">
            <div className="flex items-center gap-3">
              {/* Current Selection Display */}
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-primary">{currentKey}</span>
                <span className="text-muted-foreground">|</span>
                <span className="text-sm font-medium">{displayName}</span>
              </div>

              {/* Mode Badge */}
              <Badge
                variant={appMode === 'earTraining' ? 'secondary' : 'outline'}
                className="text-xs"
              >
                {appMode === 'earTraining' ? (
                  <><Headphones className="h-3 w-3 mr-1" />Practice</>
                ) : (
                  modeLabel
                )}
              </Badge>
            </div>

            {/* Expand/Collapse Indicator */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onSettingsClick();
                }}
              >
                <Settings className="h-4 w-4" />
              </Button>
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        {/* Expanded Controls */}
        <CollapsibleContent>
          <div className="px-3 pb-3 space-y-3 border-t bg-accent/20">
            {/* Mode Selector */}
            <div className="pt-3">
              <div className="grid grid-cols-3 gap-1">
                <Button
                  variant={appMode === 'scale' ? "default" : "outline"}
                  onClick={() => onModeChange('scale')}
                  className="text-xs h-9"
                  size="sm"
                >
                  <Music className="h-3 w-3 mr-1" />
                  Scales
                </Button>
                <Button
                  variant={appMode === 'chord' ? "default" : "outline"}
                  onClick={() => onModeChange('chord')}
                  className="text-xs h-9"
                  size="sm"
                >
                  Chords
                </Button>
                <Button
                  variant={appMode === 'earTraining' ? "default" : "outline"}
                  onClick={() => onModeChange('earTraining')}
                  className="text-xs h-9"
                  size="sm"
                >
                  <Headphones className="h-3 w-3 mr-1" />
                  Practice
                </Button>
              </div>
            </div>

            {/* Key and Accidentals Row */}
            {appMode !== 'earTraining' && (
              <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Key</label>
                  <Select value={currentKey} onValueChange={onKeyChange}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {renderKeyOptions()}
                    </SelectContent>
                  </Select>
                </div>

                {/* Accidentals Toggle */}
                <div className="grid grid-cols-2 overflow-hidden rounded-md border border-input bg-card/60 h-9">
                  <Button
                    variant={accidentalPreference === 'sharps' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-none border-0 px-3 h-full text-xs"
                    onClick={() => onAccidentalPreferenceChange('sharps')}
                  >
                    #
                  </Button>
                  <Button
                    variant={accidentalPreference === 'flats' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-none border-0 px-3 h-full text-xs"
                    onClick={() => onAccidentalPreferenceChange('flats')}
                  >
                    ♭
                  </Button>
                </div>
              </div>
            )}

            {/* Chord/Scale Type */}
            {appMode !== 'earTraining' && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  {isChord ? 'Chord Type' : 'Scale Type'}
                </label>
                <Select value={chordOrScale} onValueChange={onChordOrScaleChange}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {renderChordScaleOptions()}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Quick collapse button */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-7 text-xs text-muted-foreground"
              onClick={() => setIsExpanded(false)}
            >
              <ChevronUp className="h-3 w-3 mr-1" />
              Collapse
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default MobileControlBar;
